/*
 * Keeps the showroom card beside the appointment form in step with the showroom the
 * visitor picks inside it.
 *
 * The form is an iframe on another origin, so the page cannot read it. The form app
 * tells us what happened with postMessage instead:
 *
 *   { type: "fm-resize",    slug, height }         -- on every height change
 *   { type: "fm-change",    slug, name, value }    -- on every field change
 *   { type: "fm-submitted", slug }                 -- once, on a successful booking
 *
 * All three are handled here, the resize included: one listener that validates the
 * origin and the slug once is safer than a second, looser listener sitting beside it.
 *
 * The origin is read off the iframe's own src rather than written in. The two can then
 * never disagree -- point the section at a different form host and the check follows it,
 * instead of silently dropping every message.
 */
(() => {
  class Appointment {
    constructor(root) {
      this.root = root;
      this.frame = root.querySelector('[data-mj-appointment-frame]');
      this.cards = Array.from(root.querySelectorAll('[data-showroom]'));
      this.slug = root.dataset.mjAppointmentSlug || '';

      if (!this.frame || !this.slug) return;

      try {
        this.origin = new URL(this.frame.getAttribute('src'), location.href).origin;
      } catch (error) {
        return; /* No usable src -- nothing to listen to. */
      }

      this.show(root.dataset.mjAppointmentDefault || '');
      this.onMessage = this.onMessage.bind(this);
      addEventListener('message', this.onMessage);
    }

    /*
     * An unknown key leaves the current card alone, on purpose: a showroom the merchant
     * has not added a card for should not blank the panel out.
     */
    show(key) {
      if (!key) return;
      if (!this.cards.some((card) => card.dataset.showroom === key)) return;
      for (const card of this.cards) card.hidden = card.dataset.showroom !== key;
    }

    onMessage(event) {
      if (event.origin !== this.origin) return;

      const data = event.data;
      if (!data || typeof data !== 'object' || data.slug !== this.slug) return;

      if (data.type === 'fm-resize') {
        const height = parseFloat(data.height);
        if (height > 0) this.frame.style.height = `${height}px`;
        /* The form shrinks to its confirmation panel after it reports the booking, so the
           top of the frame has to be brought back into view once the new height is in. */
        if (this.settling) this.reveal();
        return;
      }

      if (data.type === 'fm-change') {
        /*
         * "Frisco – Stonebriar Centre" -> "Frisco". The separator is documented as a
         * spaced en dash; the pattern also accepts an em dash or a hyphen so a change of
         * punctuation in the form does not quietly stop the card switching.
         */
        if (data.name === 'showroom') {
          this.show(String(data.value).split(/\s[–—-]\s/)[0].trim());
        }
        return;
      }

      if (data.type === 'fm-submitted') {
        this.settling = true;
        clearTimeout(this.settleTimer);
        this.settleTimer = setTimeout(() => (this.settling = false), 2000);
        this.reveal();
      }
    }

    /* Top of the form (the confirmation panel is its first thing), below the sticky header. */
    reveal() {
      const smooth = !this.settling && !matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.frame.scrollIntoView({ behavior: smooth ? 'smooth' : 'auto', block: 'start' });
    }
  }

  const init = (scope) => {
    for (const root of (scope || document).querySelectorAll('[data-mj-appointment]')) {
      if (root.dataset.mjAppointmentReady) continue;
      root.dataset.mjAppointmentReady = 'true';
      new Appointment(root);
    }
  };

  init();
  /* The theme editor swaps section markup in place, so new copies need wiring up. */
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
